import { Link } from "react-router-dom";
import { Mail } from "lucide-react";
import { BRANDS, COMPANY } from "@/data/content";

// Logo hosted on Emergent customer-assets CDN (same source used in the header).
const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_rail-academy-preview/artifacts/9oj9qdyj_UKTDA.png";

const footerLinkClass =
  "text-slate-400 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-safety focus-visible:ring-offset-2 focus-visible:ring-offset-navy-deep rounded-sm";

export default function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <footer
      className="bg-navy-deep text-slate-300 mt-24"
      data-testid="site-footer"
    >
      <div className="container-rail py-16 grid gap-10 md:grid-cols-2 lg:grid-cols-4">
        <div>
          <Link
            to="/"
            className="inline-flex items-center gap-3 group"
            aria-label="UKTDA home"
            data-testid="footer-logo-link"
          >
            <img
              src={LOGO_URL}
              alt="UK Train Driver Academy Ltd logo"
              className="h-14 w-14 rounded-md object-contain bg-white p-1"
              loading="lazy"
            />

            <span className="flex flex-col leading-tight">
              <span className="font-display font-extrabold text-white text-xl tracking-tight group-hover:text-safety transition-colors">
                UKTDA
              </span>

              <span className="label-eyebrow text-[10px] text-slate-400 mt-0.5">
                Railway Training &amp; Competence
              </span>
            </span>
          </Link>

          <p className="mt-5 text-sm leading-relaxed text-slate-400 max-w-xs">
            Specialist railway training, competence development and professional
            development for the modern railway.
          </p>

          <div className="mt-6 space-y-1 text-xs text-slate-500">
            <p className="label-eyebrow text-slate-500">
              Registered in England &amp; Wales
            </p>

            {COMPANY.companyNumber && (
              <p>Company number: {COMPANY.companyNumber}</p>
            )}
          </div>
        </div>

        <nav aria-label="Company footer navigation">
          <p className="label-eyebrow text-slate-400">Company</p>

          <ul className="mt-4 space-y-2.5 text-sm">
            <li>
              <Link
                to="/about"
                className={footerLinkClass}
                data-testid="footer-link-about"
              >
                About
              </Link>
            </li>

            <li>
              <Link
                to="/our-brands"
                className={footerLinkClass}
                data-testid="footer-link-brands"
              >
                Our Brands
              </Link>
            </li>

            <li>
              <Link
                to="/training"
                className={footerLinkClass}
                data-testid="footer-link-training"
              >
                Training &amp; Courses
              </Link>
            </li>

            <li>
              <Link
                to="/professional-development"
                className={footerLinkClass}
                data-testid="footer-link-pd"
              >
                Professional Development
              </Link>
            </li>

            <li>
              <Link
                to="/future-driver"
                className={footerLinkClass}
                data-testid="footer-link-future-driver"
              >
                Future Driver
              </Link>
            </li>

            <li>
              <Link
                to="/glossary"
                className={footerLinkClass}
                data-testid="footer-link-glossary"
              >
                Glossary
              </Link>
            </li>

            <li>
              <Link
                to="/contact"
                className={footerLinkClass}
                data-testid="footer-link-contact"
              >
                Contact
              </Link>
            </li>
          </ul>
        </nav>

        <nav aria-label="UKTDA brand links">
          <p className="label-eyebrow text-slate-400">Our Brands</p>

          <ul className="mt-4 space-y-2.5 text-sm">
            {BRANDS.map((brand) => (
              <li key={brand.slug || brand.name}>
                <a
                  href={brand.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={footerLinkClass}
                  data-testid={`footer-brand-${brand.slug}`}
                  aria-label={`${brand.name} website opens in a new tab`}
                >
                  {brand.name}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <nav aria-label="Legal and contact footer navigation">
          <p className="label-eyebrow text-slate-400">Legal &amp; Contact</p>

          <ul className="mt-4 space-y-2.5 text-sm">
            <li>
              <Link
                to="/legal/privacy"
                className={footerLinkClass}
                data-testid="footer-link-privacy"
              >
                Privacy Policy
              </Link>
            </li>

            <li>
              <Link
                to="/legal/terms"
                className={footerLinkClass}
                data-testid="footer-link-terms"
              >
                Terms
              </Link>
            </li>

            <li>
              <button
                type="button"
                onClick={() =>
                  window.dispatchEvent(new Event("uktda:open-consent"))
                }
                className={`${footerLinkClass} bg-transparent border-0 p-0 cursor-pointer text-left`}
                data-testid="footer-cookie-settings"
              >
                Cookie settings
              </button>
            </li>

            {COMPANY.email && (
              <li>
                <a
                  href={`mailto:${COMPANY.email}`}
                  className={`${footerLinkClass} inline-flex items-center justify-center rounded-md border border-slate-700/70 p-2 hover:border-safety/60`}
                  data-testid="footer-email"
                  aria-label="Email UK Train Driver Academy Ltd"
                  title="Email UK Train Driver Academy Ltd"
                >
                  <Mail className="h-4 w-4" aria-hidden="true" />
                  <span className="sr-only">
                    Email UK Train Driver Academy Ltd
                  </span>
                </a>
              </li>
            )}
          </ul>
        </nav>
      </div>

      <div className="border-t border-slate-700/60">
        <div className="container-rail py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-xs text-slate-500">
          <p>
            &copy; {currentYear} {COMPANY.legalName}. All rights reserved.
          </p>

          <div className="flex items-center gap-5">
            <p className="font-mono-ibm tracking-widest uppercase">
              Safety-critical learning &middot; UK rail
            </p>

            <Link
              to="/learn/admin/login"
              className="text-slate-600 transition-colors hover:text-slate-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-safety rounded-sm"
              data-testid="footer-link-admin"
              aria-label="TRAC-Learn administration login"
            >
              Admin
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}